import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button } from "../../components/common";
import { APIError } from "../../lib/api";
import styles from "./ImportPodcastPage.module.css";

interface FeedPreview {
  title: string;
  description: string | null;
  coverArtworkUrl: string | null;
  author: string | null;
  language: string;
  categories: string[];
  explicit: boolean;
  websiteUrl: string | null;
  episodeCount: number;
  episodes: Array<{
    title: string;
    publishedAt: string | null;
    audioDurationSeconds: number | null;
    episodeType: string;
  }>;
}

interface ImportJob {
  id: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  totalEpisodes: number;
  completedEpisodes: number;
  failedEpisodes: number;
  errorMessage: string | null;
  podcastId: string | null;
}

type Step = "url" | "preview" | "importing" | "complete";

const TOTAL_STEPS = 4;
const STEP_INDEX: Record<Step, number> = { url: 0, preview: 1, importing: 2, complete: 3 };

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options?.headers },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) {
    throw new APIError(data?.message ?? "Request failed", res.status, data);
  }
  return data as T;
}

function formatDuration(seconds: number | null): string {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}:${String(s).padStart(2, "0")}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ImportPodcastPage() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("url");
  const [feedUrl, setFeedUrl] = useState("");
  const [preview, setPreview] = useState<FeedPreview | null>(null);
  const [importJob, setImportJob] = useState<ImportJob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handlePreview = useCallback(async () => {
    if (!feedUrl.trim() || !orgId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJSON<FeedPreview>(
        `/api/organizations/${orgId}/podcasts/import/preview`,
        { method: "POST", body: JSON.stringify({ feedUrl: feedUrl.trim() }) },
      );
      setPreview(data);
      setStep("preview");
    } catch (err) {
      const msg = err instanceof APIError ? err.message : "Failed to fetch feed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [feedUrl, orgId]);

  const handleImport = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const job = await fetchJSON<ImportJob>(`/api/organizations/${orgId}/podcasts/import`, {
        method: "POST",
        body: JSON.stringify({ feedUrl: feedUrl.trim() }),
      });
      setImportJob(job);
      setStep("importing");

      // Start polling for progress
      pollRef.current = setInterval(async () => {
        try {
          const updated = await fetchJSON<ImportJob>(
            `/api/organizations/${orgId}/import-jobs/${job.id}`,
          );
          setImportJob(updated);
          if (updated.status === "completed" || updated.status === "failed") {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            if (updated.status === "completed") {
              setStep("complete");
            } else {
              setError(updated.errorMessage ?? "Import failed");
            }
          }
        } catch {
          // Silently retry on poll errors
        }
      }, 2000);
    } catch (err) {
      const msg = err instanceof APIError ? err.message : "Failed to start import";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [orgId, feedUrl]);

  const handleCancel = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setStep("url");
    setPreview(null);
    setImportJob(null);
    setError(null);
  }, []);

  const progress =
    importJob && importJob.totalEpisodes > 0
      ? Math.round(
          ((importJob.completedEpisodes + importJob.failedEpisodes) / importJob.totalEpisodes) *
            100,
        )
      : 0;

  return (
    <>
      <Header />
      <main className={styles.container}>
        <div className={styles.backSection}>
          <Link to="/dashboard" className={styles.backLink}>
            ← Back to dashboard
          </Link>
        </div>

        <div className={styles.card}>
          <h1>Import Podcast</h1>
          <p className={styles.description}>Import an existing podcast from its RSS feed.</p>

          {/* Step indicators */}
          <div className={styles.steps}>
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div
                key={i}
                className={`${styles.step} ${
                  i < STEP_INDEX[step]
                    ? styles.stepCompleted
                    : i === STEP_INDEX[step]
                      ? styles.stepActive
                      : ""
                }`}
              />
            ))}
          </div>

          {error && <div className={styles.error}>{error}</div>}

          {/* Step 1: Enter URL */}
          {step === "url" && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="feedUrl">
                  RSS Feed URL
                </label>
                <input
                  id="feedUrl"
                  type="url"
                  className={styles.input}
                  placeholder="https://example.com/feed.rss"
                  value={feedUrl}
                  onChange={(e) => setFeedUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handlePreview();
                  }}
                  disabled={loading}
                />
              </div>
              <div className={styles.actions}>
                <Button
                  variant="primary"
                  onClick={handlePreview}
                  disabled={!feedUrl.trim() || loading}
                >
                  {loading ? (
                    <>
                      <span className={styles.spinner} /> Fetching…
                    </>
                  ) : (
                    "Preview Feed"
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Preview */}
          {step === "preview" && preview && (
            <>
              <div className={styles.preview}>
                {preview.coverArtworkUrl ? (
                  <img
                    src={preview.coverArtworkUrl}
                    alt={preview.title}
                    className={styles.artwork}
                  />
                ) : (
                  <div className={styles.artworkPlaceholder}>🎙️</div>
                )}
                <div className={styles.previewInfo}>
                  <h2>{preview.title}</h2>
                  {preview.author && <p>by {preview.author}</p>}
                  {preview.description && (
                    <p>
                      {preview.description.length > 200
                        ? `${preview.description.slice(0, 200)}…`
                        : preview.description}
                    </p>
                  )}
                  <div className={styles.meta}>
                    <span className={styles.metaItem}>
                      <strong>{preview.episodeCount}</strong> episodes
                    </span>
                    <span className={styles.metaItem}>
                      Language: <strong>{preview.language}</strong>
                    </span>
                    {preview.categories.length > 0 && (
                      <span className={styles.metaItem}>{preview.categories.join(", ")}</span>
                    )}
                  </div>
                </div>
              </div>

              {preview.episodes.length > 0 && (
                <div className={styles.episodeList}>
                  <h3>
                    Recent episodes{" "}
                    {preview.episodeCount > 5 && `(showing 5 of ${preview.episodeCount})`}
                  </h3>
                  {preview.episodes.map((ep, i) => (
                    <div key={i} className={styles.episodeItem}>
                      <span className={styles.episodeTitle}>{ep.title}</span>
                      <span className={styles.episodeMeta}>
                        {formatDuration(ep.audioDurationSeconds)} · {formatDate(ep.publishedAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                <Button variant="secondary" onClick={handleCancel}>
                  Back
                </Button>
                <Button variant="primary" onClick={handleImport} disabled={loading}>
                  {loading ? (
                    <>
                      <span className={styles.spinner} /> Starting…
                    </>
                  ) : (
                    "Import Podcast"
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Importing */}
          {step === "importing" && importJob && (
            <div className={styles.progressSection}>
              <p className={styles.progressStatus}>
                {importJob.status === "running"
                  ? "Importing episodes…"
                  : importJob.status === "failed"
                    ? "Import failed"
                    : "Starting import…"}
              </p>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
              <p className={styles.progressText}>
                {importJob.completedEpisodes} of {importJob.totalEpisodes} episodes
                {importJob.failedEpisodes > 0 && ` (${importJob.failedEpisodes} failed)`}
              </p>
              <div className={styles.actions}>
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === "complete" && importJob && (
            <div className={styles.success}>
              <div className={styles.successIcon}>🎉</div>
              <h2>Import Complete!</h2>
              <p>
                Successfully imported {importJob.completedEpisodes} episode
                {importJob.completedEpisodes !== 1 ? "s" : ""}
                {importJob.failedEpisodes > 0 && ` (${importJob.failedEpisodes} failed)`}.
              </p>
              <div className={styles.actions} style={{ justifyContent: "center" }}>
                {importJob.podcastId && (
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(`/organizations/${orgId}/podcasts/${importJob.podcastId}`)
                    }
                  >
                    View Podcast
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/organizations/${orgId}/podcasts`)}
                >
                  Back to Podcasts
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
